import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import { useDispatch } from 'react-redux';
import { toObject } from '../data-utils';
import { AttributeData, CrmRecord, CrmSettings } from '../model';
import { actions } from '../store';
import Button from './Button';
import { openWindowCrmLink } from './CrmIntegrationView';
import css from './CrmContactSelectionView.module.scss';
import ToastPortal from './ToasterPortal';
import { ReactComponent as CrmAccountIcon } from './icons/crm/crm-account.svg';
import { ReactComponent as CrmContactIcon } from './icons/crm/crm-contact.svg';

export default function CrmContactSelectionView({ crmSettings, records, createPhoneActivity }: {
  crmSettings: CrmSettings
  records: CrmRecord[]
  createPhoneActivity: (crmRecord: CrmRecord | null) => Promise<void>
}) {
  const dispatch = useDispatch()

  function renderAttribute(attr: AttributeData | undefined, className?: string, useLabel: boolean = true) {
    if (attr === undefined) return null

    if (useLabel) {
      return <span className={className}>{attr.displayName}: {attr.value}</span>
    } else {
      return <span className={className}>{attr.value}</span>
    }
  }

  function renderSubTitle(attr: AttributeData, attr2: AttributeData) {
    if (attr === undefined && attr2 === undefined) {
      return null
    } else if (attr && attr2) {
      return <span className={css.subtitle}>{attr.value}, {attr2.value}</span>
    } else {
      const validAttr = attr ?? attr2
      return <span className={css.subtitle}>{validAttr.value}</span>
    }
  }

  function renderGender(attr: AttributeData | undefined) {
    if (attr === undefined) return null

    const genderAttribute = { ...attr, value: attr.value === '1' ? 'Male' : attr.value === '2' ? 'Female' : null }
    if (genderAttribute.value === null) return null

    return renderAttribute(genderAttribute, css.normalText)
  }

  function renderAddress(attr: AttributeData | undefined) {
    if (attr === undefined) return null

    attr = { ...attr, value: attr.value?.replaceAll('\r\n', ', ') ?? null, displayName: 'Address' }
    if (attr.value === null) return null

    return renderAttribute(attr, css.normalText)
  }

  function renderBirthday(attr: AttributeData | undefined) {
    if (attr === undefined) return null

    const date = moment(attr.value);
    attr = { ...attr, value: date.toDate().toLocaleDateString() }
    return renderAttribute(attr, css.normalText)
  }

  function renderTitleBar(title: AttributeData | undefined, Icon: React.FC, containerClassName?: string) {
    return (<div className={css.TitleBarContainer}>
      <div className={classNames(css.IconContainer, containerClassName)}>
        <Icon />
      </div>
      {renderAttribute(title, css.title, false)}
    </div>)
  }

  function renderContact(record: CrmRecord) {
    const unwantedFields: string[] = [
      'contactid',
      'firstname',
      'middlename',
      'lastname',
      'address1_line1',
      'address1_line2',
      'address1_line3',
      'address1_city',
      'address1_postalcode',
      'address1_country',
    ]

    const wantedFields: string[] = [
      'fullname',
      'parentcustomerid',
      'jobtitle',
      'birthdate',
      'gendercode',
      'address1_composite',   // this is actually generated, not explicitly declared by the server
      'emailaddress1',
      'telephone1',
      'telephone2',
      'telephone3',
      'mobilephone',
    ]

    const filteredAttributes = record.attributes.filter(attr => !unwantedFields.find(unwanted => unwanted === attr.id))
    const filteredFields = toObject(filteredAttributes, key => key.id, item => item)
    const customFields = filteredAttributes.filter(attr => !wantedFields.find(wanted => wanted === attr.id))

    return (<Button key={record.contactId} onClick={() => {
      openWindowCrmLink(record.profileUrl)
      if (crmSettings.popPhoneCall) {
        createPhoneActivity(record)
      }
      dispatch(actions.setCrmContacts(null))
    }}>
      {renderTitleBar(filteredFields['fullname'], CrmContactIcon, css.contactContainer)}
      {renderSubTitle(filteredFields['parentcustomerid'], filteredFields['jobtitle'])}
      {renderGender(filteredFields['gendercode'])}
      {renderBirthday(filteredFields['birthdate'])}
      {renderAttribute(filteredFields['emailaddress1'], css.normalText)}
      {renderAttribute(filteredFields['telephone1'], css.normalText)}
      {renderAttribute(filteredFields['telephone2'], css.normalText)}
      {renderAttribute(filteredFields['telephone3'], css.normalText)}
      {renderAttribute(filteredFields['mobilephone'], css.normalText)}
      {renderAddress(filteredFields['address1_composite'])}

      {customFields.length > 0 && <span className={css.otherDetailsHeader}>Other Details:</span>}
      {customFields.map(attr => renderAttribute(attr, css.normalText))}
    </Button>)
  }

  function renderAccount(record: CrmRecord) {
    const unwantedFields: string[] = [
      "accountid",
      "primarycontactid",
      "address1_line1",
      "address1_line2",
      "address1_line3",
      "address1_city",
      "address1_postalcode",
      "address1_country",
    ]

    const wantedFields: string[] = [
      "name",
      'address1_composite',   // this is actually generated, not explicitly declared by the server
      'emailaddress1',
      'telephone1',
      'telephone2',
      'telephone3',
    ]

    const filteredAttributes = record.attributes.filter(attr => !unwantedFields.find(unwanted => unwanted === attr.id))
    const filteredFields = toObject(filteredAttributes, key => key.id, item => item)
    const customFields = filteredAttributes.filter(attr => !wantedFields.find(wanted => wanted === attr.id))

    return (<Button key={record.contactId} onClick={() => {
      openWindowCrmLink(record.profileUrl)
      if (crmSettings.popPhoneCall) {
        createPhoneActivity(record)
      }
      dispatch(actions.setCrmContacts(null))
    }}>
      {renderTitleBar(filteredFields['name'], CrmAccountIcon, css.accountContainer)}
      {renderAttribute(filteredFields['emailaddress1'], css.normalText)}
      {renderAttribute(filteredFields['telephone1'], css.normalText)}
      {renderAttribute(filteredFields['telephone2'], css.normalText)}
      {renderAttribute(filteredFields['telephone3'], css.normalText)}
      {renderAddress(filteredFields['address1_composite'])}

      {customFields.length > 0 && <span className={css.otherDetailsHeader}>Other Details:</span>}
      {customFields.map(attr => renderAttribute(attr, css.normalText))}
    </Button>)
  }

  function renderCrmRecord(record: CrmRecord) {
    return record.type === 'Account' ? renderAccount(record) : renderContact(record)
  }

  return <>
    <ToastPortal id='-crm-profile-select' toastName='My Test CRM toast' isToaster={false} width={1280} height={768} className={css.CallPopupContainer} isQueueCall={false}>
      <h2>Please select the following contacts:</h2>
      {records.map(renderCrmRecord)}
      <Button className={css.negativeButton} onClick={() => {
        openWindowCrmLink(crmSettings.defaultNoContactResultPage)
        if (crmSettings.popPhoneCall) {
          createPhoneActivity(null)
        }
        dispatch(actions.setCrmContacts(null))
      }}>
        <span className={classNames(css.normalText)}>None of the above</span>
      </Button>
    </ToastPortal>
  </>
}
